import { ArrowDown, ArrowUp, Minus } from '@phosphor-icons/react';
import { Text } from '@fragments-sdk/ui';
import { formatRuleLabelSentence } from '@/lib/govern-rule-catalog';
import s from './dashboard.module.scss';

export type TrendDirection = 'up-good' | 'down-good';

export interface TrendBadgeProps {
  /** The delta value to display (positive, negative, or zero). */
  delta: number;
  /** Optional label suffix (e.g. "this week"). */
  label?: string;
  /**
   * Whether an increase is positive news.
   * - `up-good`   — enrichment, coverage, tokens added (↑ = green)
   * - `down-good` — drift caught, open issues (↓ = green)
   */
  direction?: TrendDirection;
  /** Display as a percentage (appends %). */
  percent?: boolean;
  /** Display as a signed number (prepends +/-). */
  signed?: boolean;
}

export function TrendBadge({
  delta,
  label,
  direction = 'up-good',
  percent = false,
  signed = true,
}: TrendBadgeProps) {
  const isZero = delta === 0;
  const isPositive = delta > 0;
  const good = direction === 'up-good' ? isPositive : !isPositive && !isZero;
  const tone = isZero ? 'neutral' : good ? 'good' : 'bad';
  const Icon = isZero ? Minus : isPositive ? ArrowUp : ArrowDown;
  const magnitude = Math.abs(delta);
  const formatted = percent ? `${magnitude}%` : String(magnitude);
  const sign = signed && !isZero ? (isPositive ? '+' : '-') : '';

  return (
    <Text
      as="span"
      size="2xs"
      weight="medium"
      tabularNums
      className={s.trendBadge}
      data-tone={tone}
    >
      <Icon size={10} weight="bold" />
      <Text as="span" size="2xs" weight="medium" tabularNums>
        {sign}
        {formatted}
      </Text>
      {label ? (
        <Text as="span" size="2xs" className={s.trendBadgeLabel}>
          {label}
        </Text>
      ) : null}
    </Text>
  );
}

export function SparkBg({ color, data }: { color: string; data: number[] }) {
  const pts =
    data.length > 1 && data.some((v) => v > 0) ? data : [0, 0, 0, 0, 0, 0, 0];
  const w = 200;
  const h = 60;
  const max = Math.max(...pts);
  const min = Math.min(...pts);
  const range = max - min || 1;
  const d = pts
    .map((v, i) => {
      const x = (i / (pts.length - 1)) * w;
      const y = h - ((v - min) / range) * (h - 4) - 2;
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
  const areaD = `${d} L ${w} ${h} L 0 ${h} Z`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className={s.sparkBg}
    >
      <defs>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.15" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaD} fill="url(#sparkFill)" />
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.4"
      />
    </svg>
  );
}

export function gradeColor(grade: string): string {
  if (grade === 'A' || grade === 'B') {
    return 'var(--fui-color-success, #22c55e)';
  }
  if (grade === 'C') {
    return 'var(--fui-color-warning, #eab308)';
  }
  return 'var(--fui-color-danger, #ef4444)';
}

export function formatRuleLabel(ruleId: string): string {
  return formatRuleLabelSentence(ruleId);
}

export function formatTimeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
